import React from "react";
import MarkdownIt from "markdown-it";

import { ListCard } from "@/shared/components/ListCard";

import AnswerImage from "./AnswerImage";
import Citation from "./Citation";
import CodeBlock, { CopyButton } from "./CodeBlock";
import Diagram from "./Diagram";
import Link from "./Link";
import math, { blankFormulas, type Formula } from "./math";
import MathFormula from "./MathFormula";

/* Markdown as CommonMark plus GitHub's tables, strikethrough, task lists and bare links, parsed
   by markdown-it and drawn as React in the editorial look. Shared by the chat transcript, the
   client app, the extension, Knowledge previews and the Skills page. Raw HTML shows as the text
   it is and only web and mail links open (`Link`). Fenced code is `CodeBlock`, a ```mermaid
   fence its diagram (`Diagram`), drawn once the fence closes; a table sits in the same card in a
   tray; formulas (`math.ts`) are `MathFormula`, an https picture is `AnswerImage`, and a
   citation, `[2]`, is its source's chip (`Citation`) where the answer was given its sources.

   Streaming, the whole text is parsed on every update and drawn block by block (the parser's
   top-level blocks); a finished block keeps its nodes, so only the one being written is drawn
   again. That one hides the marks it has opened and not yet closed (`settled`), so nothing
   shows raw and then jumps into shape. Ceiling: a parse per update is linear in the answer's
   length, measured at 1.2 ms for 10 KB and 5.4 ms for 50 KB on a laptop; at one update a frame
   an answer past about 100 KB would start to cost frames. */

const md = new MarkdownIt({ linkify: true }).use(math);
// every link is parsed, so one that may not open still shows its words (see `Link`)
md.validateLink = () => true;

// GitHub's "www.example.com" links, without the trailing punctuation or an unpaired ")". Other
// bare domains stay text, or every README.md and config.py would be a link.
md.linkify.add("www.", {
  validate: (text, pos) => {
    let tail = /^[\w-]+(\.[\w-]+)+[^\s<]*/.exec(text.slice(pos))?.[0] ?? "";
    for (let was = ""; was !== tail; ) {
      was = tail;
      tail = tail.replace(/[?!.,:*_~]+$/, "");
      if (tail.endsWith(")") && tail.split("(").length < tail.split(")").length) tail = tail.slice(0, -1);
    }
    return tail.length;
  },
  normalize: (match) => {
    match.url = `http://${match.url}`;
  },
});

// GitHub's task lists: "- [ ] a" and "- [x] a". The item keeps whether it is done, its words lose
// the box. Runs last, after the item's text is joined into one token.
md.core.ruler.push("task_lists", (state) => {
  state.tokens.forEach((item, i) => {
    const first = state.tokens[i + 2]?.children?.[0]; // list_item_open, paragraph_open, inline
    const box = item.type === "list_item_open" && first?.type === "text" ? /^\[([ xX])\](?:[ \t]+|$)/.exec(first.content) : null;
    if (!first || !box) return;
    item.meta = { done: box[1] !== " " };
    first.content = first.content.slice(box[0].length);
  });
});

// a picture inside a link opens the link, not a full view of itself
md.core.ruler.push("linked_images", (state) => {
  state.tokens.forEach((block) => {
    let depth = 0;
    block.children?.forEach((token) => {
      depth += token.type === "link_open" ? 1 : token.type === "link_close" ? -1 : 0;
      if (token.type === "image") token.meta = { linked: depth > 0 };
    });
  });
});

type Token = ReturnType<typeof md.parse>[number];
type Node = { token: Token; children: Node[] };

// "[2]", "[1, 3]"; "[2](" is a link that didn't parse, not a citation
const CITATIONS = /\[(\d{1,3}(?:\s*,\s*\d{1,3})*)\](?!\()/g;

// citations, in the words once everything else is read: a link's own words ("[[1]](…)"), a
// reference link ("[1]" with "[1]: …") and code stay what they are. Before `text_join`, which
// would merge an escaped "\[1\]" back into plain words. `Citation` draws each, or leaves it as
// the words it is where the answer has no such source.
md.core.ruler.before("text_join", "citations", (state) => {
  for (const block of state.tokens) {
    if (block.type !== "inline" || !block.children) continue;
    let linkDepth = 0;
    block.children = block.children.flatMap((token) => {
      linkDepth += token.type === "link_open" ? 1 : token.type === "link_close" ? -1 : 0;
      return token.type === "text" && linkDepth === 0 ? withCitations(token, state.Token) : [token];
    });
  }
});

/** A run of words split around its citations, each one a `citation` token with its numbers. */
function withCitations(text: Token, MakeToken: new (type: string, tag: string, nesting: -1 | 0 | 1) => Token): Token[] {
  const words = (content: string) => Object.assign(new MakeToken("text", "", 0), { content });
  const out: Token[] = [];
  let at = 0;
  for (const found of text.content.matchAll(CITATIONS)) {
    if (found.index > at) out.push(words(text.content.slice(at, found.index)));
    out.push(Object.assign(new MakeToken("citation", "", 0), { content: found[0], meta: { ids: found[1].split(",").map(Number) } }));
    at = found.index + found[0].length;
  }
  if (!out.length) return [text];
  if (at < text.content.length) out.push(words(text.content.slice(at)));
  return out;
}

/** The numbers a text cites, in the order it first cites them, read as `Markdown` draws them. */
export function citedNumbers(text: string): number[] {
  const found = new Set<number>();
  for (const block of md.parse(text, {})) {
    for (const token of block.children ?? []) {
      if (token.type === "citation") (token.meta as { ids: number[] }).ids.forEach((id) => found.add(id));
    }
  }
  return [...found];
}

// a fence's line, also inside a quote or on a list item's first line
const FENCE = /^(?:[ \t]*(?:>|(?:[-+*]|\d+[.)])[ \t]))*[ \t]*(```|~~~)/;
// the closing fence still being typed, one or two of its marks in
const HALF_CLOSER = /\n(?:[ \t]*>)*[ \t]*(`{1,2}|~{1,2})[ \t]*$/;
const TABLE_SEPARATOR = /^\s*\|?[\s:|-]+\|?\s*$/;
const HEADING: Record<string, string> = {
  h1: "text-[20px] pt-2", h2: "text-[17.5px] pt-2", h3: "text-[16px] pt-1",
  h4: "text-[15px] pt-1", h5: "text-[14px]", h6: "text-[14px] text-[var(--text-tertiary)]",
};

export default React.memo(function Markdown({ text, streaming = false }: { text: string; streaming?: boolean }) {
  const env = {};
  const blocks = blocksOf(text, md.parse(text, env));
  if (streaming && blocks.length) {
    const writing = blocks[blocks.length - 1];
    writing.tokens = md.parse(settled(writing.source, writing.tokens), env);
    // an unclosed fence runs to the end, so it is the block's last
    const open = writing.source.split("\n").filter((line) => FENCE.test(line)).length % 2 === 1;
    const fence = writing.tokens.filter((token) => token.type === "fence").pop();
    if (open && fence) fence.meta = { writing: true };
  }
  // a reference link's address can arrive after the block that uses it
  const references = JSON.stringify(env);
  return (
    <div className="editorial space-y-3 break-words min-w-0">
      {blocks.map((block, i) => (
        <Block key={i} {...block} references={references} streaming={streaming && i === blocks.length - 1} />
      ))}
    </div>
  );
});

type BlockProps = { source: string; tokens: Token[]; references: string; streaming: boolean };

/** One top-level block; a finished one keeps its nodes while the next one is written. */
const Block = React.memo(
  function Block({ tokens, streaming }: BlockProps) {
    return <>{draw(treeOf(tokens), streaming)}</>;
  },
  (was, now) => was.source === now.source && was.references === now.references && was.streaming === now.streaming,
);

/** The text's top-level blocks, each with its own source lines and tokens. */
function blocksOf(text: string, tokens: Token[]): { source: string; tokens: Token[] }[] {
  const lines = text.split("\n");
  const blocks: { source: string; tokens: Token[] }[] = [];
  let start = 0;
  tokens.forEach((token, i) => {
    if (token.level !== 0 || token.nesting === 1) return; // a block ends at its top-level close or is one token
    const map = tokens[start].map ?? [0, 0];
    blocks.push({ source: lines.slice(map[0], map[1]).join("\n"), tokens: tokens.slice(start, i + 1) });
    start = i + 1;
  });
  return blocks;
}

/** A block still being written, without what it has opened and not closed: an unpaired `**`,
    `__`, `~~` or backtick, a link before its `)`, a table before its first row, and a last line
    that is only a marker (it would turn the line above into a heading, or draw an empty item or
    a fence's first backticks). Code shows as it comes, but not a closing fence half typed, and
    marks inside code don't count. Ceiling: it counts marks; it does not parse nesting. */
function settled(source: string, tokens: Token[]): string {
  if (tokens[0].type === "code_block") return source;
  const lines = source.split("\n");
  // a fence still open, at the top or inside a list or quote: its code is coming
  if (tokens[0].type === "fence" || lines.filter((line) => FENCE.test(line)).length % 2) return source.replace(HALF_CLOSER, "");
  // a table shows once its separator row is whole, which the next line starting proves
  const row = lines.findIndex((line) => line.trim().startsWith("|"));
  if (row >= 0 && !lines.slice(row + 1, -1).some((line) => TABLE_SEPARATOR.test(line))) lines.splice(row);
  let text = lines.join("\n");
  let prose = proseOf(text);
  for (const mark of ["**", "__", "~~", "`"]) {
    if (prose.split(mark).length % 2 === 0) {
      const cut = prose.lastIndexOf(mark);
      [text, prose] = [text.slice(0, cut), prose.slice(0, cut)];
    }
  }
  return withoutHalfLink(text, prose).replace(/(^|\n)(?:[ \t]*>)*[ \t]*([-+*=`~]+|\d+[.)])[ \t]*$/, ""); // also inside a quote
}

/** The text with its code and formulas blanked to spaces, the same length, so a `**kwargs`, a
    backtick inside code or a `[` inside TeX isn't a mark left open. */
function proseOf(text: string): string {
  let inside = false;
  const blanked = text
    .split("\n")
    .map((line) => {
      const fence = FENCE.test(line);
      if (fence) inside = !inside;
      return fence || inside ? " ".repeat(line.length) : line;
    })
    .join("\n")
    .replace(/`[^`\n]+`/g, (code) => " ".repeat(code.length));
  return blankFormulas(blanked);
}

/** The text without a link or picture still being written: "[its words" or "[its words](https://exa",
    an address's own brackets counted, as in "(javascript:alert(1)". Read on its prose, cut on the text. */
function withoutHalfLink(text: string, prose: string): string {
  const open = prose.lastIndexOf("[");
  // an escaped "\[" opens no link (it may open a formula)
  const half = open < 0 || prose[open - 1] === "\\" ? null : /^\[[^\]\n]*(?:\]\(([^\n]*))?$/.exec(prose.slice(open));
  if (!half) return text;
  let depth = 0;
  for (const char of half[1] ?? "") {
    depth += char === "(" ? 1 : char === ")" ? -1 : 0;
    if (depth < 0) return text; // its ")" came: a whole link
  }
  return text.slice(0, prose[open - 1] === "!" ? open - 1 : open);
}

/** markdown-it's flat run of open and close tokens, as a tree. */
function treeOf(tokens: Token[]): Node[] {
  const root: Node[] = [];
  const open = [root];
  for (const token of tokens) {
    if (token.nesting === -1) {
      open.pop();
      continue;
    }
    const node = { token, children: token.children ? treeOf(token.children) : [] };
    open[open.length - 1].push(node);
    if (token.nesting === 1) open.push(node.children);
  }
  return root;
}

function draw(nodes: Node[], streaming: boolean): React.ReactNode[] {
  return nodes.map((node, key) => drawNode(node, key, streaming));
}

function drawNode({ token, children }: Node, key: number, streaming: boolean): React.ReactNode {
  const inside = () => draw(children, streaming);
  switch (token.type) {
    case "inline":
      return <React.Fragment key={key}>{inside()}</React.Fragment>;
    case "text":
      return <React.Fragment key={key}>{words(token.content, streaming)}</React.Fragment>;
    case "paragraph_open":
      // a tight list's items hold their words without a paragraph
      return token.hidden ? <React.Fragment key={key}>{inside()}</React.Fragment> : <p key={key} className="m-0">{inside()}</p>;
    case "heading_open": {
      const Heading = token.tag as "h1";
      return (
        <Heading key={key} className={`${HEADING[token.tag]} font-semibold tracking-[-0.015em] text-[var(--text-primary)] first:pt-0`}>
          {inside()}
        </Heading>
      );
    }
    case "blockquote_open":
      return (
        <blockquote key={key} className="border-l-2 border-[var(--hairline-strong)] pl-4 space-y-2 text-[var(--text-tertiary)]">
          {inside()}
        </blockquote>
      );
    case "bullet_list_open":
    case "ordered_list_open":
      return <List key={key} list={token} items={children} streaming={streaming} />;
    case "table_open":
      return (
        <ListCard key={key} label="Table" action={<CopyButton text={tableText(children)} label="Copy table" />}>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[14px] tabular-nums">{inside()}</table>
          </div>
        </ListCard>
      );
    case "thead_open":
      return <thead key={key}>{inside()}</thead>;
    case "tbody_open":
      return (
        <tbody key={key}>
          {children.map((row, i) => (
            <tr key={i} className={i < children.length - 1 ? "border-b hairline" : undefined}>{draw(row.children, streaming)}</tr>
          ))}
        </tbody>
      );
    case "tr_open":
      return <tr key={key}>{inside()}</tr>;
    case "th_open":
      return (
        <th key={key} style={aligned(token)} className="text-left font-medium text-[13px] text-[var(--text-tertiary)] px-4 py-2.5 border-b border-r last:border-r-0 hairline whitespace-nowrap">
          {inside()}
        </th>
      );
    case "td_open":
      return <td key={key} style={aligned(token)} className="px-4 py-2.5 align-top border-r last:border-r-0 hairline">{inside()}</td>;
    case "hr":
      return <hr key={key} className="my-3 border-0 border-t hairline" />;
    case "fence":
    case "code_block": {
      const code = token.content.replace(/\n$/, "");
      const lang = token.info.trim().split(/\s+/)[0];
      if (lang.toLowerCase() === "mermaid") return <Diagram key={key} source={code} writing={!!token.meta?.writing} />;
      return <CodeBlock key={key} code={code} lang={lang} />;
    }
    case "math":
    case "math_block":
      return <MathFormula key={key} {...(token.meta as Formula)} />;
    case "code_inline":
      return (
        <code key={key} className="px-[6px] py-[1px] rounded-[6px] bg-[var(--surface-2)] text-[0.87em] text-[var(--text-primary)]">
          {words(token.content, streaming)}
        </code>
      );
    case "strong_open":
      return <strong key={key} className="font-semibold text-[var(--text-primary)]">{inside()}</strong>;
    case "em_open":
      return <em key={key}>{inside()}</em>;
    case "s_open":
      return <s key={key} className="text-[var(--text-tertiary)]">{inside()}</s>;
    case "link_open":
      return <Link key={key} href={String(token.attrGet("href"))}>{inside()}</Link>;
    case "citation":
      return <Citation key={key} ids={(token.meta as { ids: number[] }).ids} words={token.content} />;
    case "image":
      return <AnswerImage key={key} src={String(token.attrGet("src"))} alt={token.content} linked={!!token.meta?.linked} />;
    case "softbreak": // a line break in an answer is meant, as in a chat message
    case "hardbreak":
      return <br key={key} />;
    default:
      return null;
  }
}

/** A table's cells as words, a tab between cells and a line per row: pasted into a sheet, a table. */
function tableText(sections: Node[]): string {
  const plain = (nodes: Node[]): string =>
    nodes.map(({ token, children }) => (children.length ? plain(children) : token.type === "math" ? (token.meta as Formula).source : token.content)).join("");
  return sections.flatMap((section) => section.children.map((row) => row.children.map((cell) => plain(cell.children)).join("\t"))).join("\n");
}

function words(text: string, streaming: boolean): React.ReactNode {
  if (!streaming) return text;
  return text.split(/(\s+)/).map((word, i) => (/\S/.test(word) ? <span key={i} className="stream-word">{word}</span> : word));
}

function aligned(cell: Token): React.CSSProperties | undefined {
  const align = /text-align:(left|center|right)/.exec(String(cell.attrGet("style") ?? ""));
  return align ? { textAlign: align[1] as "left" } : undefined;
}

function List({ list, items, streaming }: { list: Token; items: Node[]; streaming: boolean }) {
  const ordered = list.type === "ordered_list_open";
  const start = Number(list.attrGet("start") ?? 1);
  return React.createElement(
    ordered ? "ol" : "ul",
    { className: "space-y-1.5", style: { listStyle: "none" }, start: ordered ? start : undefined },
    items.map(({ token, children }, i) => {
      const task = token.meta as { done: boolean } | null;
      return (
        <li key={i} className="flex gap-2">
          {task ? (
            <input type="checkbox" checked={task.done} readOnly disabled aria-label={task.done ? "Done" : "Not done"} className="shrink-0 mt-[5px] size-[14px] accent-[var(--text-primary)]" />
          ) : ordered ? (
            <span className="shrink-0 min-w-[1.25rem] text-right text-[var(--text-tertiary)] tabular-nums">{start + i}{list.markup}</span>
          ) : (
            <span aria-hidden className="shrink-0 w-[1.25rem] flex justify-center pt-[0.66em]">
              <span className="size-[5px] rounded-full bg-[var(--text-quaternary)]" />
            </span>
          )}
          <div className="min-w-0 flex-1 space-y-1.5">{draw(children, streaming)}</div>
        </li>
      );
    }),
  );
}
